import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  getPOSSales,
  getPOSSaleById,
  updatePOSDeliveryStatus,
  updatePharmacySaleStatus,
} from "../../apis/pos";
import { processPayment } from "../../apis/payment";
import { POSSale } from "../../interfaces/POS";
import {
  Tag,
  Button,
  Modal,
  message,
  Dropdown,
} from "antd";
import { format } from "date-fns";
import { PrinterOutlined } from "@ant-design/icons";
import { useNotification } from "../../NotificationContext";
import { getPharmacyBillHtml } from "../../apis/receipts";
import CommonPagination from "../../components/CommanPagination";
import PaymentModal, { PaymentData } from "../../components/PaymentModal";

const getStatusColor = (status: string) => {
  switch (status?.toLowerCase()) {
    case "completed":
    case "paid":
      return "green";
    case "pending":
      return "orange";
    case "cancelled":
    case "rejected":
      return "red";
    case "dispatched":
      return "blue";
    case "received":
      return "purple";
    case "partially_paid":
      return "blue";
    default:
      return "default";
  }
};

const getStatusLabel = (status: string) => {
  if (!status) return "-";
  return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
};

const isCurrentMonth = (dateStr: string | undefined): boolean => {
  if (!dateStr) return false;
  const d = new Date(dateStr);
  const now = new Date();
  return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
};

interface POSSaleResponse extends POSSale {
  patient?: { id: number; firstName: string; lastName: string };
  visitId?: number;
  visit_id?: number;
  saleNumber?: string;
  saleDate?: string;
  totalAmount?: string;
  taxableValue?: string;
  gstAmount?: string;
  discountAmount?: string;
  taxAmount?: string;
  netAmount?: string;
  itemsCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

const POSList: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [meta, setMeta] = useState<any>();
  const [posSales, setPosSales] = useState<POSSaleResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedSale, setSelectedSale] = useState<POSSaleResponse | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [updatingDeliverySaleId, setUpdatingDeliverySaleId] = useState<number | null>(null);

  // Payment modal state
  const [paymentSale, setPaymentSale] = useState<POSSaleResponse | null>(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);

  useEffect(() => {
    fetchPOSSales({ page: 1, per_page: 10 });
  }, []);

  const fetchPOSSales = (params = {}) => {
    setLoading(true);
    getPOSSales({ per_page: 10, ...params })
      .then((res: any) => {
        const salesData = res.data?.data || res.data || [];
        const metaData = res.meta || res.data?.meta || {};
        setPosSales(Array.isArray(salesData) ? salesData : []);
        setMeta(metaData);
      })
      .catch(() => {
        showNotification("error", "Failed to fetch POS sales", "", "topRight");
      })
      .finally(() => setLoading(false));
  };

  const handleViewSale = (saleId: number) => {
    getPOSSaleById(saleId)
      .then((res: any) => {
        setSelectedSale(res.data?.data || res.data || res);
        setIsViewModalOpen(true);
      })
      .catch(() =>
        showNotification("error", "Failed to fetch sale details", "", "topRight")
      );
  };

  const formatCurrency = (amount: number | string) => {
    const n = typeof amount === "string" ? parseFloat(amount) : amount;
    return `₹${(n || 0).toFixed(2)}`;
  };

  const getTaxAmount = (sale: any) => {
    const explicit = sale.taxAmount ?? sale.tax_amount ?? sale.tax ?? null;
    if (explicit !== null && explicit !== undefined && explicit !== "") return Number(explicit) || 0;
    const total = Number(sale.totalAmount ?? sale.total_amount ?? 0) || 0;
    const discount = Number(sale.discountAmount ?? sale.discount_amount ?? 0) || 0;
    const net = Number(sale.netAmount ?? sale.net_amount ?? sale.total ?? 0) || 0;
    return Math.max(0, net - total + discount);
  };

  const getTaxableValue = (sale: any) => {
    const explicit = sale.taxableValue ?? sale.taxable_value ?? null;
    if (explicit !== null && explicit !== undefined && explicit !== "") return Number(explicit) || 0;
    const total = Number(sale.totalAmount ?? sale.total_amount ?? 0) || 0;
    const discount = Number(sale.discountAmount ?? sale.discount_amount ?? 0) || 0;
    return Math.max(0, total - discount);
  };

  const getSaleNetAmount = (sale: any): number =>
    Number(sale.netAmount ?? sale.net_amount ?? sale.total ?? 0) || 0;

  const getDeliveryMenuItems = (sale: POSSaleResponse) => {
    const isIPD = Boolean(sale.visitId ?? (sale as any).visit_id);
    const delivery = ((sale.deliveryStatus ?? (sale as any).delivery_status ?? "pending") as string).toLowerCase();
    const items: any[] = [];
    if (isIPD) {
      if (delivery === "pending") items.push({ key: "delivery_dispatched", label: "Dispatched" });
      return { deliveryChildren: items };
    }
    if (delivery === "pending") {
      items.push(
        { key: "delivery_completed", label: "Completed" },
        { key: "delivery_dispatched", label: "Dispatched" },
        { key: "delivery_received", label: "Received" },
        { key: "delivery_rejected", label: "Rejected" }
      );
    } else if (delivery === "dispatched") {
      items.push({ key: "delivery_received", label: "Received" });
    } else if (delivery === "completed") {
      items.push(
        { key: "delivery_dispatched", label: "Dispatched" },
        { key: "delivery_received", label: "Received" },
        { key: "delivery_rejected", label: "Rejected" }
      );
    }
    return { deliveryChildren: items };
  };

  const handleChangeDeliveryStatus = (
    saleId: number,
    newStatus: "pending" | "dispatched" | "received" | "completed" | "rejected"
  ) => {
    setUpdatingDeliverySaleId(saleId);
    updatePOSDeliveryStatus(saleId, newStatus)
      .then(() => {
        message.success("Delivery status updated");
        setPosSales((prev) =>
          prev.map((s) => (s.id === saleId ? { ...s, deliveryStatus: newStatus } : s))
        );
      })
      .catch(() => message.error("Failed to update delivery status"))
      .finally(() => setUpdatingDeliverySaleId(null));
  };

  const handlePaymentSave = async (paymentData: PaymentData) => {
    if (!paymentSale?.id) return;

    const netAmount = getSaleNetAmount(paymentSale);
    const roundedNetAmount = +(netAmount + (paymentData.roundOff ?? 0)).toFixed(2);
    const totalPaid =
      (paymentData.cash || 0) +
      (paymentData.upi || 0) +
      (paymentData.card || 0) +
      (paymentData.cheque || 0) +
      (paymentData.online || 0) +
      (paymentData.advanceUsed || 0) +
      (paymentData.insuranceUsed || 0);

    const newStatus: "pending" | "paid" | "partially_paid" =
      totalPaid === 0 ? "pending" : totalPaid >= roundedNetAmount ? "paid" : "partially_paid";

    try {
      const payments: any[] = [];
      const payDate = new Date().toISOString().split("T")[0];
      const txnRef = paymentData.transactionRef || undefined;

      if (paymentData.cash > 0)
        payments.push({ amount: paymentData.cash, payment_mode: "cash", payment_date: payDate, notes: paymentData.paymentNote || null, allocation_type: "manual" });
      if (paymentData.upi > 0)
        payments.push({ amount: paymentData.upi, payment_mode: "upi", payment_date: payDate, transaction_id: txnRef, notes: paymentData.paymentNote || null, allocation_type: "manual" });
      if (paymentData.card > 0)
        payments.push({ amount: paymentData.card, payment_mode: "card", payment_date: payDate, transaction_id: txnRef, notes: paymentData.paymentNote || null, allocation_type: "manual" });
      if (paymentData.cheque > 0)
        payments.push({ amount: paymentData.cheque, payment_mode: "cheque", payment_date: payDate, transaction_id: txnRef, notes: paymentData.paymentNote || null, allocation_type: "manual" });
      if (paymentData.online > 0)
        payments.push({ amount: paymentData.online, payment_mode: "online", payment_date: payDate, transaction_id: txnRef, notes: paymentData.paymentNote || null, allocation_type: "manual" });
      if ((paymentData.advanceUsed || 0) > 0)
        payments.push({ amount: paymentData.advanceUsed, payment_mode: "cash", payment_date: payDate, notes: "Advance allocation", allocation_type: "manual" });
      if ((paymentData.insuranceUsed || 0) > 0)
        payments.push({ amount: paymentData.insuranceUsed, payment_mode: "insurance", payment_date: payDate, notes: "Insurance cashless payment", allocation_type: "manual" });

      if (payments.length > 0) {
        await processPayment({
          source_type: "pharmacy_sale",
          source_ids: [paymentSale.id!],
          payments,
          source_amounts: { [paymentSale.id!]: roundedNetAmount },
          invoice_date: payDate,
        });
      }

      await updatePharmacySaleStatus(paymentSale.id!, newStatus);

      message.success("Payment recorded successfully");
      setIsPaymentModalOpen(false);
      setPaymentSale(null);
      fetchPOSSales({ page: meta?.current_page || 1, per_page: meta?.per_page || 10 });
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || "Payment failed");
    }
  };

  const onPaginationChange = (page: number, pageSize?: number) => {
    fetchPOSSales({ page, per_page: pageSize || 10 });
  };

  const buildMenuItems = (sale: POSSaleResponse) => {
    const { deliveryChildren } = getDeliveryMenuItems(sale);
    const canEdit = isCurrentMonth(sale.saleDate ?? sale.createdAt);
    const isPending = sale.status === "pending" || sale.status === "partially_paid";

    const items: any[] = [{ key: "view", label: "View" }];

    if (isPending) {
      items.push({ key: "make_payment", label: "Make Payment" });
    }

    items.push({
      key: "edit",
      label: canEdit ? "Edit" : "Edit (locked — past month)",
      disabled: !canEdit,
    });

    if (deliveryChildren && deliveryChildren.length > 0) {
      items.push(
        { type: "divider" as const },
        { key: "delivery", label: "Delivery Status", children: deliveryChildren }
      );
    }

    return items;
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="sm:flex sm:items-center sm:justify-between">
        <div>
          <h1 className="text-base font-semibold leading-6 text-gray-900">POS Sales</h1>
          <p className="mt-1 text-sm text-gray-500">Pharmacy point-of-sale transactions.</p>
        </div>
        <button
          type="button"
          onClick={() => navigate("/pharmacy/pos")}
          className="mt-4 sm:mt-0 px-3 py-2 text-sm font-semibold text-white rounded-md shadow-sm bg-primary hover:bg-hover"
        >
          New Sale
        </button>
      </div>

      <div className="flow-root mt-8">
        <div className="overflow-x-auto -mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block py-2 min-w-full align-middle sm:px-6 lg:px-8">
            <div className="overflow-hidden ring-1 ring-black ring-opacity-5 shadow sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Sale</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Items</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Taxable</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">GST</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Total</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Payment</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Delivery</th>
                    <th className="py-3.5 px-3 text-left text-sm font-semibold text-gray-900">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500">Loading...</td>
                    </tr>
                  ) : posSales.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-500">No sales found</td>
                    </tr>
                  ) : (
                    posSales.map((sale) => (
                      <tr key={sale.id} className="hover:bg-gray-50">
                        <td className="py-4 pr-3 pl-4 text-sm sm:pl-6">
                          <div className="font-medium text-gray-900 flex items-center gap-2">
                            {sale.saleNumber || `#${sale.id}`}
                            {sale.visitId ?? (sale as any).visit_id ? <Tag color="blue">IPD</Tag> : null}
                          </div>
                          <div className="text-gray-500">
                            {sale.patient
                              ? `${sale.patient.firstName || ""} ${sale.patient.lastName || ""}`.trim() || "N/A"
                              : "Walk-in"}
                          </div>
                          <div className="text-xs text-gray-400">
                            {sale.saleDate
                              ? format(new Date(sale.saleDate), "dd MMM yyyy")
                              : sale.createdAt
                              ? format(new Date(sale.createdAt), "dd MMM yyyy")
                              : "-"}
                          </div>
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-700">{sale.itemsCount ?? 0}</td>
                        <td className="px-3 py-4 text-sm text-gray-700">{formatCurrency(getTaxableValue(sale))}</td>
                        <td className="px-3 py-4 text-sm text-gray-700">{formatCurrency(getTaxAmount(sale))}</td>
                        <td className="px-3 py-4 text-sm font-semibold text-gray-900">
                          {formatCurrency(getSaleNetAmount(sale))}
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <Tag color={getStatusColor(sale.status || "")}>
                            {getStatusLabel(sale.status || "")}
                          </Tag>
                        </td>
                        <td className="px-3 py-4 text-sm">
                          <Tag color={getStatusColor(sale.deliveryStatus || "")}>
                            {getStatusLabel(sale.deliveryStatus || "")}
                          </Tag>
                        </td>
                        <td className="px-3 py-4 text-sm">
                          {sale.status === "cancelled" ? (
                            <Button disabled size="small">Actions</Button>
                          ) : (
                            <Dropdown
                              menu={{
                                items: buildMenuItems(sale),
                                onClick: ({ key }) => {
                                  if (key === "view") {
                                    handleViewSale(sale.id!);
                                  } else if (key === "make_payment") {
                                    setPaymentSale(sale);
                                    setIsPaymentModalOpen(true);
                                  } else if (key === "edit") {
                                    navigate(`/pharmacy/pos/edit/${sale.id}`);
                                  } else if (key.startsWith("delivery_")) {
                                    handleChangeDeliveryStatus(
                                      sale.id!,
                                      key.replace("delivery_", "") as any
                                    );
                                  }
                                },
                              }}
                              placement="bottomLeft"
                            >
                              <Button size="small" loading={updatingDeliverySaleId === sale.id}>
                                Actions
                              </Button>
                            </Dropdown>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <CommonPagination meta={meta} onChange={onPaginationChange} />
          </div>
        </div>
      </div>

      {/* View Sale Modal */}
      <Modal
        title="Sale Details"
        open={isViewModalOpen}
        onCancel={() => { setIsViewModalOpen(false); setSelectedSale(null); }}
        footer={[
          <Button key="close" onClick={() => { setIsViewModalOpen(false); setSelectedSale(null); }}>
            Close
          </Button>,
          <Button
            key="print"
            type="primary"
            icon={<PrinterOutlined />}
            onClick={async () => {
              if (!selectedSale?.id) return;
              try {
                const res: any = await getPharmacyBillHtml({ pharmacySaleId: selectedSale.id });
                const blobUrl = URL.createObjectURL(new Blob([res.data], { type: "text/html" }));
                window.open(blobUrl, "_blank");
                setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
              } catch {
                message.error("Failed to open print receipt");
              }
            }}
          >
            Print Receipt
          </Button>,
        ]}
        width={800}
      >
        {selectedSale && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-700">Sale Number</p>
                <p className="text-sm text-gray-900">
                  {(selectedSale as any).sale_number ?? selectedSale.saleNumber ?? `#${selectedSale.id}`}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Date</p>
                <p className="text-sm text-gray-900">
                  {((selectedSale as any).sale_date ?? selectedSale.saleDate ?? selectedSale.createdAt)
                    ? format(new Date((selectedSale as any).sale_date ?? selectedSale.saleDate ?? selectedSale.createdAt!), "dd MMM yyyy")
                    : "-"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Patient</p>
                <p className="text-sm text-gray-900">
                  {selectedSale.patient
                    ? `${(selectedSale.patient as any).first_name ?? selectedSale.patient.firstName ?? ""} ${(selectedSale.patient as any).last_name ?? selectedSale.patient.lastName ?? ""}`.trim() || "N/A"
                    : "Walk-in"}
                </p>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-700">Status</p>
                <Tag color={getStatusColor(selectedSale.status || "")}>{getStatusLabel(selectedSale.status || "")}</Tag>
              </div>
            </div>

            <div>
              <p className="mb-2 text-sm font-semibold text-gray-700">Items</p>
              <div className="overflow-hidden rounded-lg border">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {["Item", "Qty", "Price", "Discount", "Tax %", "Tax Amt", "Total"].map((h) => (
                        <th key={h} className="px-4 py-2 text-xs font-medium text-left text-gray-700">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {selectedSale.items?.map((item: any, index: number) => {
                      const pharmacyItem = item.pharmacy_item || item.pharmacyItem;
                      const batch = item.batch;
                      const unitPrice = Number(item.selling_price ?? item.sellingPrice ?? item.mrp ?? 0);
                      const qty = Number(item.quantity ?? 1);
                      const discountAmt = Number(item.discount_amount ?? item.discountAmount ?? 0);
                      const gross = unitPrice * qty;
                      const taxableAfterDiscount = Math.max(0, gross - discountAmt);
                      const taxPct = Number(item.tax_percentage ?? item.taxPercentage ?? 0);
                      let taxAmt = Number(item.tax_amount ?? item.taxAmount ?? 0);
                      if (!taxAmt && taxPct > 0) taxAmt = taxableAfterDiscount - taxableAfterDiscount / (1 + taxPct / 100);
                      const netAmt = item.net_amount ?? item.netAmount ?? item.total ?? taxableAfterDiscount;
                      return (
                        <tr key={item.id ?? index}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            <p className="font-medium">{pharmacyItem?.name ?? item.name ?? "-"}</p>
                            {batch?.batch_number && (
                              <p className="text-xs text-gray-400">Batch: {batch.batch_number}</p>
                            )}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">{qty}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(unitPrice)}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(discountAmt)}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{taxPct > 0 ? `${taxPct}%` : "-"}</td>
                          <td className="px-4 py-2 text-sm text-gray-700">{formatCurrency(taxAmt)}</td>
                          <td className="px-4 py-2 text-sm font-medium text-gray-900">{formatCurrency(netAmt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="pt-4 border-t space-y-1">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Total Amount</span>
                <span>{formatCurrency((selectedSale as any).total_amount ?? selectedSale.totalAmount ?? selectedSale.total ?? 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Discount</span>
                <span>{formatCurrency((selectedSale as any).discount_amount ?? selectedSale.discountAmount ?? selectedSale.discount ?? 0)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Tax</span>
                <span>{formatCurrency((selectedSale as any).tax_amount ?? selectedSale.taxAmount ?? selectedSale.tax ?? 0)}</span>
              </div>
              <div className="flex justify-between text-lg font-semibold pt-2 border-t">
                <span>Net Amount</span>
                <span>{formatCurrency(getSaleNetAmount(selectedSale))}</span>
              </div>
            </div>

            {selectedSale.payments && selectedSale.payments.length > 0 && (
              <div>
                <p className="mb-2 text-sm font-semibold text-gray-700">Payments</p>
                <div className="space-y-2">
                  {selectedSale.payments.map((payment, i) => (
                    <div key={i} className="flex justify-between items-center p-2 bg-gray-50 rounded">
                      <p className="text-sm font-medium text-gray-900 capitalize">
                        {payment.paymentMode.replace(/-/g, " ")}
                      </p>
                      <p className="text-sm font-semibold">{formatCurrency(payment.amount)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selectedSale.notes && (
              <div>
                <p className="text-sm font-semibold text-gray-700">Notes</p>
                <p className="text-sm text-gray-900">{selectedSale.notes}</p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Make Payment Modal */}
      <PaymentModal
        isOpen={isPaymentModalOpen}
        onClose={() => { setIsPaymentModalOpen(false); setPaymentSale(null); }}
        total={paymentSale ? getSaleNetAmount(paymentSale) : 0}
        discount={0}
        sourceType="pharmacy"
        sourceId={paymentSale?.id}
        patientId={paymentSale?.patient?.id}
        patientName={
          paymentSale?.patient
            ? `${paymentSale.patient.firstName || ""} ${paymentSale.patient.lastName || ""}`.trim()
            : "Walk-in"
        }
        allowPartialOrUnpaid
        onSave={handlePaymentSave}
      />
    </div>
  );
};

export default POSList;
